<template>
  <div>
    <div class="d-flex w-100 justify-space-between align-baseline">
      <div>
        <div class="d-flex align-baseline mb-1 mt-3">
          <div class="headline5">
            {{ $t("cloudBackupAndRestore.title") }}
          </div>
          <powered-by-keepit />
        </div>
        <div
          v-if="dataCenter"
          class="body1 text-indigo-medium"
          data-testid="backup-and-restore-data-center-label"
        >
          {{ $t("cloudBackupAndRestore.connected.dataCenter") }}:
          <span class="text-uppercase">{{ dataCenter }}</span>
        </div>
      </div>
      <v-btn
        v-if="showChildList && showCreateAction"
        color="primary"
        data-testid="backup-and-restore-create-customer-account-btn"
        rounded
        size="large"
        @click="openCreateCustomerAccount()"
      >
        {{ $t("cloudBackupAndRestore.connected.emptyState.createAccount") }}
      </v-btn>
    </div>

    <v-skeleton-loader
      :loading="showSkeletonLoader"
      :type="coronetSkeletonLoaderTypes.DEFAULT_TABLE"
    >
      <!--
        Show the filters + child-workspace table once there is more than one child,
        or whenever a filter is active (so a filtered-down/empty result keeps the
        filters visible instead of falling back to the create-account empty state).
      -->
      <child-workspace-list
        v-if="showChildList"
        :items="children"
        :pagination="pagination"
        :total-items="totalChildren"
        :loading="loadingChildren"
        @filters-changed="onFiltersChanged"
        @page-changed="onPageChanged"
        @refresh="fetchChildren()"
      />

      <!-- No child workspaces yet -> empty state + create entry point. -->
      <empty-state v-else class="margin-auto mt-12">
        <template #icon>
          <v-icon size="80" icon="$backupAndRestoreL" />
        </template>
        <template #description>
          <span>{{ $t("cloudBackupAndRestore.connected.emptyState.title") }}</span>
        </template>
        <template #subtitle>
          <span>{{ $t("cloudBackupAndRestore.connected.emptyState.subtitle") }}</span>
        </template>
        <template #button>
          <v-btn
            color="primary"
            data-testid="backup-and-restore-create-customer-account-btn"
            rounded
            size="large"
            v-if="showCreateAction"
            @click="openCreateCustomerAccount()"
          >
            {{ $t("cloudBackupAndRestore.connected.emptyState.createAccount") }}
          </v-btn>
        </template>
      </empty-state>
    </v-skeleton-loader>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import isEqual from "lodash/isEqual";
import EmptyState from "@/components/EmptyState.vue";
import PoweredByKeepit from "@/components/PoweredByKeepit.vue";
import ChildWorkspaceList from "@/components/msp/cloud-backup/ChildWorkspaceList.vue";
import CreateCustomerAccountModal from "@/components/modals/msp/cloud-backup/CreateCustomerAccountModal.vue";
import { useDialogsStore } from "@/_store/dialogs.module";
import {
  type CreateBackupChildPayload,
  useMspCloudBackupStore,
} from "@/_store/msp-cloud-backup.module.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers";
import { BackupAndRestoreAction } from "@/constants/cloud-backup.ts";
import { ModalWidth } from "@/constants/modals";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { coronetSkeletonLoaderTypes } from "@/constants/skeleton-loader.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";
import type { Pagination } from "@/types";
import { isMspActionRestricted } from "@/_helpers/msp-permissions.ts";

export default defineComponent({
  components: {
    EmptyState,
    PoweredByKeepit,
    ChildWorkspaceList,
  },
  props: {
    dataCenter: {
      type: String,
      default: undefined,
    },
  },
  emits: ["refresh"],
  setup() {
    const dialogsStore = useDialogsStore();
    const backupAndRestoreStore = useMspCloudBackupStore();

    const { children, totalChildren, pagination } = storeToRefs(backupAndRestoreStore);
    const loadingChildren = ref(false);
    const showChildList = ref(false);
    const showSkeletonLoader = ref(false);
    const fetchChildren = async (loading = false) => {
      showSkeletonLoader.value = loading;
      loadingChildren.value = true;
      try {
        await backupAndRestoreStore.getChildren();
      } finally {
        loadingChildren.value = false;
        showSkeletonLoader.value = false;
      }
    };

    const showCreateAction = computed(
      () =>
        !isMspActionRestricted(
          RolePermissionsScope.PROTECTION,
          SubscriptionModule.BACKUP_AND_RESTORE
        )
    );

    // A narrowed result set can leave the current page out of range, so start over
    // from the first page whenever the filters change.
    const onFiltersChanged = () => {
      backupAndRestoreStore.resetPagination();
      return fetchChildren();
    };

    const onPageChanged = (newPagination: Pagination) => {
      // The table announces its options once on mount and again whenever the bound
      // page changes, so ignore the echoes that match what was already requested.
      if (isEqual(newPagination, pagination.value)) {
        return;
      }
      backupAndRestoreStore.setPagination(newPagination);
      return fetchChildren();
    };

    onMounted(async () => {
      await fetchChildren(true);
      showChildList.value = totalChildren.value > 0;
    });

    const openCreateCustomerAccount = () => {
      dialogsStore.openDialog({
        ...componentDialogsConfigConstructor({
          action: BackupAndRestoreAction.CREATE_CUSTOMER_ACCOUNT,
          component: CreateCustomerAccountModal,
          width: ModalWidth.LARGE,
          disable: !showCreateAction.value,
          callback: async (payload: CreateBackupChildPayload) => {
            await backupAndRestoreStore.createChild(payload);
            // Go back to the first page so the freshly created account is on screen.
            backupAndRestoreStore.resetPagination();
            await fetchChildren();
            showChildList.value = true;
          },
        }),
        footer: null,
      });
    };

    return {
      children,
      coronetSkeletonLoaderTypes,
      loadingChildren,
      showChildList,
      totalChildren,
      pagination,
      showSkeletonLoader,
      showCreateAction,
      fetchChildren,
      onFiltersChanged,
      onPageChanged,
      openCreateCustomerAccount,
    };
  },
});
</script>
